export const BREAKPOINT_ORDER = Object.freeze([
    'base',
    'sm',
    'md',
    'lg',
    'xl',
    '2xl',
]);
export const NON_BASE_BREAKPOINT_ORDER = Object.freeze(BREAKPOINT_ORDER.filter((breakpoint) => breakpoint !== 'base'));
export const BREAKPOINT_MIN_WIDTH = Object.freeze({
    sm: '40rem',
    md: '48rem',
    lg: '64rem',
    xl: '80rem',
    '2xl': '96rem',
});
export const BREAKPOINT_SET = new Set(BREAKPOINT_ORDER);
function compareStrings(left, right) {
    if (left < right) {
        return -1;
    }
    if (left > right) {
        return 1;
    }
    return 0;
}
const BREAKPOINT_LENGTH_PATTERN = /^(-?(?:\d+\.?\d*|\.\d+))(px|rem|em|vw|vh|svw|svh|lvw|lvh|dvw|dvh)$/;
const sortedBreakpointScaleCache = new WeakMap();
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function hasBreakpointScaleShape(value) {
    return isPlainObject(value) && Object.values(value).every((entry) => typeof entry === 'string');
}
function parseBreakpointLength(value) {
    const match = BREAKPOINT_LENGTH_PATTERN.exec(value.trim());
    if (!match) {
        return undefined;
    }
    const numericValue = Number(match[1]);
    const unit = match[2];
    switch (unit) {
        case 'px':
            return {
                category: 'font-relative',
                value: numericValue,
            };
        case 'rem':
        case 'em':
            return {
                category: 'font-relative',
                value: numericValue * 16,
            };
        case 'vw':
        case 'svw':
        case 'lvw':
        case 'dvw':
            return {
                category: 'viewport-width',
                value: numericValue,
            };
        case 'vh':
        case 'svh':
        case 'lvh':
        case 'dvh':
            return {
                category: 'viewport-height',
                value: numericValue,
            };
        default:
            return undefined;
    }
}
function compareIndexedBreakpointEntries(left, right) {
    if (left.parsed && right.parsed && left.parsed.category === right.parsed.category) {
        if (left.parsed.value !== right.parsed.value) {
            return left.parsed.value - right.parsed.value;
        }
        const keyCompare = compareStrings(left.key, right.key);
        if (keyCompare !== 0) {
            return keyCompare;
        }
    }
    return left.index - right.index;
}
function normalizeBreakpointEntries(scale) {
    return Object.entries(scale)
        .map(([key, value], index) => ({
        key,
        value,
        index,
        parsed: parseBreakpointLength(value),
    }))
        .sort(compareIndexedBreakpointEntries);
}
export function sortBreakpointScale(scale) {
    const cached = sortedBreakpointScaleCache.get(scale);
    if (cached) {
        return cached;
    }
    const sorted = Object.freeze(Object.fromEntries(normalizeBreakpointEntries(scale).map(({ key, value }) => [key, value])));
    sortedBreakpointScaleCache.set(scale, sorted);
    sortedBreakpointScaleCache.set(sorted, sorted);
    return sorted;
}
export function resolveBreakpointScale(input) {
    if (!input) {
        return BREAKPOINT_MIN_WIDTH;
    }
    if (isPlainObject(input.breakpoints)) {
        return sortBreakpointScale(input.breakpoints);
    }
    if (hasBreakpointScaleShape(input)) {
        return sortBreakpointScale(input);
    }
    return BREAKPOINT_MIN_WIDTH;
}
export function getBreakpointOrder(input) {
    return ['base', ...Object.keys(resolveBreakpointScale(input))];
}
export function getNonBaseBreakpointOrder(input) {
    return Object.keys(resolveBreakpointScale(input));
}
export function isBreakpointKey(value, input) {
    return getBreakpointOrder(input).includes(value);
}
function toPascalCaseSegment(value) {
    return value.length === 0 ? value : `${value[0].toUpperCase()}${value.slice(1)}`;
}
function resolveMaxWidth(maxWidthExclusive) {
    return `calc(${maxWidthExclusive} - 0.02px)`;
}
function resolveBreakpointIndex(breakpoint, input) {
    if (breakpoint === 'base') {
        return -1;
    }
    return getNonBaseBreakpointOrder(input).indexOf(breakpoint);
}
function resolveNextBreakpoint(breakpoint, input) {
    const orderedBreakpoints = getNonBaseBreakpointOrder(input);
    const index = orderedBreakpoints.indexOf(breakpoint);
    return index >= 0 ? orderedBreakpoints[index + 1] : undefined;
}
function resolvePreviousBreakpoint(breakpoint, input) {
    const orderedBreakpoints = getNonBaseBreakpointOrder(input);
    const index = orderedBreakpoints.indexOf(breakpoint);
    return index > 0 ? orderedBreakpoints[index - 1] : undefined;
}
export function resolveHideBelowCondition(breakpoint, input) {
    if (!isBreakpointKey(breakpoint, input) || breakpoint === 'base') {
        return undefined;
    }
    const previousBreakpoint = resolvePreviousBreakpoint(breakpoint, input);
    return previousBreakpoint ? `${previousBreakpoint}Down` : 'baseOnly';
}
export function resolveResponsiveCondition(condition, input) {
    const breakpoints = resolveBreakpointScale(input);
    if (condition === 'base') {
        return { name: condition };
    }
    if (condition === 'baseOnly') {
        const firstBreakpoint = getNonBaseBreakpointOrder(input)[0];
        if (!firstBreakpoint) {
            return undefined;
        }
        return {
            name: condition,
            mediaQuery: `(max-width: ${resolveMaxWidth(breakpoints[firstBreakpoint])})`,
            endBreakpoint: 'base',
        };
    }
    if (Object.prototype.hasOwnProperty.call(breakpoints, condition)) {
        return {
            name: condition,
            mediaQuery: `(min-width: ${breakpoints[condition]})`,
            startBreakpoint: condition,
        };
    }
    for (const breakpoint of getNonBaseBreakpointOrder(input)) {
        if (condition === `${breakpoint}Only`) {
            const nextBreakpoint = resolveNextBreakpoint(breakpoint, input);
            return {
                name: condition,
                mediaQuery: nextBreakpoint
                    ? `(min-width: ${breakpoints[breakpoint]}) and (max-width: ${resolveMaxWidth(breakpoints[nextBreakpoint])})`
                    : `(min-width: ${breakpoints[breakpoint]})`,
                startBreakpoint: breakpoint,
                endBreakpoint: breakpoint,
            };
        }
        if (condition === `${breakpoint}Down`) {
            const nextBreakpoint = resolveNextBreakpoint(breakpoint, input);
            if (!nextBreakpoint) {
                return undefined;
            }
            return {
                name: condition,
                mediaQuery: `(max-width: ${resolveMaxWidth(breakpoints[nextBreakpoint])})`,
                endBreakpoint: breakpoint,
            };
        }
    }
    const orderedBreakpoints = getNonBaseBreakpointOrder(input);
    for (const startBreakpoint of orderedBreakpoints) {
        for (const endBreakpoint of orderedBreakpoints) {
            const startIndex = resolveBreakpointIndex(startBreakpoint, input);
            const endIndex = resolveBreakpointIndex(endBreakpoint, input);
            if (startIndex < 0 || endIndex < startIndex) {
                continue;
            }
            if (condition !== `${startBreakpoint}To${toPascalCaseSegment(endBreakpoint)}`) {
                continue;
            }
            const nextBreakpoint = resolveNextBreakpoint(endBreakpoint, input);
            return {
                name: condition,
                mediaQuery: nextBreakpoint
                    ? `(min-width: ${breakpoints[startBreakpoint]}) and (max-width: ${resolveMaxWidth(breakpoints[nextBreakpoint])})`
                    : `(min-width: ${breakpoints[startBreakpoint]})`,
                startBreakpoint,
                endBreakpoint,
            };
        }
    }
    return undefined;
}
export function isResponsiveConditionKey(value, input) {
    return resolveResponsiveCondition(value, input) !== undefined;
}
export function isResponsiveValueRecord(value, input) {
    return isPlainObject(value) && Object.keys(value).some((key) => isResponsiveConditionKey(key, input));
}
export function listResponsiveConditions(value, input) {
    const keys = Object.keys(value).filter((key) => isResponsiveConditionKey(key, input));
    const orderedBreakpointKeys = getBreakpointOrder(input).filter((key) => keys.includes(key));
    const derivedKeys = keys
        .filter((key) => key !== 'base' && !orderedBreakpointKeys.includes(key))
        .sort(compareStrings);
    return [...orderedBreakpointKeys, ...derivedKeys];
}
export function toResponsiveValueRecord(value, input) {
    if (Array.isArray(value)) {
        const responsiveValue = {};
        const orderedBreakpoints = getBreakpointOrder(input);
        for (const [index, breakpoint] of orderedBreakpoints.entries()) {
            if (!(index in value)) {
                continue;
            }
            responsiveValue[breakpoint] = value[index];
        }
        return responsiveValue;
    }
    if (isResponsiveValueRecord(value, input)) {
        return value;
    }
    return undefined;
}
export function compareResponsiveConditionNames(left, right, input) {
    const leftCondition = resolveResponsiveCondition(left, input);
    const rightCondition = resolveResponsiveCondition(right, input);
    const leftStart = leftCondition?.startBreakpoint
        ? resolveBreakpointIndex(leftCondition.startBreakpoint, input)
        : Number.MIN_SAFE_INTEGER;
    const rightStart = rightCondition?.startBreakpoint
        ? resolveBreakpointIndex(rightCondition.startBreakpoint, input)
        : Number.MIN_SAFE_INTEGER;
    if (leftStart !== rightStart) {
        return leftStart - rightStart;
    }
    const leftEnd = leftCondition?.endBreakpoint
        ? resolveBreakpointIndex(leftCondition.endBreakpoint, input)
        : Number.MAX_SAFE_INTEGER;
    const rightEnd = rightCondition?.endBreakpoint
        ? resolveBreakpointIndex(rightCondition.endBreakpoint, input)
        : Number.MAX_SAFE_INTEGER;
    if (leftEnd !== rightEnd) {
        return rightEnd - leftEnd;
    }
    return compareStrings(left, right);
}
export function resolveResponsiveValue(value, fallbackValue, input) {
    const responsiveValue = toResponsiveValueRecord(value, input);
    if (!responsiveValue) {
        return (value ?? fallbackValue);
    }
    if (Object.prototype.hasOwnProperty.call(responsiveValue, 'base')) {
        return (responsiveValue.base ?? fallbackValue);
    }
    return fallbackValue;
}
export function withResponsiveBaseFallback(value, fallbackValue, input) {
    const responsiveValue = toResponsiveValueRecord(value, input);
    if (!responsiveValue) {
        return (value ?? fallbackValue);
    }
    if (Object.prototype.hasOwnProperty.call(responsiveValue, 'base')) {
        return responsiveValue;
    }
    return {
        base: fallbackValue,
        ...responsiveValue,
    };
}
//# sourceMappingURL=breakpoints.js.map